import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import EmailIcon from '@mui/icons-material/Email';
import api from './api';
import qs from 'qs';
import { useUser } from './UserContext';
import { useTiers } from './TiersContext';

const ContestForm = ({ isGalleryMode: isGalleryModeProp = false }) => {
  const { user } = useUser();
  const tiers = useTiers();
  const { clubShortName, contestShortName } = useParams();
  const navigate = useNavigate();

  // Determine if we're in edit mode
  const isEditMode = !!contestShortName;

  // Gallery mode: set from prop (create) or from loaded contest data (edit)
  const [isGallery, setIsGallery] = useState(isGalleryModeProp);

  // Form state
  const [contestName, setContestName] = useState('');
  const [contestShortNameValue, setContestShortNameValue] = useState('');
  const [contestDescription, setContestDescription] = useState('');
  const [contestUploadsStart, setContestUploadsStart] = useState(null);
  const [contestUploadsEnd, setContestUploadsEnd] = useState(null);
  const [contestVotesStart, setContestVotesStart] = useState(null);
  const [contestVotesEnd, setContestVotesEnd] = useState(null);
  const [contestMaxUploads, setContestMaxUploads] = useState(10);
  const [contestMaxVotes, setContestMaxVotes] = useState(10);
  const [contestCommittee, setContestCommittee] = useState([]);
  const [contestShowResults, setContestShowResults] = useState(false);
  const [contestGalleryEndDate, setContestGalleryEndDate] = useState(null);

  // Validation state
  const [shortNameStatus, setShortNameStatus] = useState('');
  const [shortNameError, setShortNameError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [debounceTimer, setDebounceTimer] = useState(null);

  // Club and contest data
  const [club, setClub] = useState(null);
  const [contest, setContest] = useState(null);
  const [loading, setLoading] = useState(true);

  const [dateError, setDateError] = useState('');

  // Contest limit state
  const [oldestContest, setOldestContest] = useState(null);
  const [isAtContestLimit, setIsAtContestLimit] = useState(false);
  const [canDownloadOldest, setCanDownloadOldest] = useState(false);

  // Permission states
  const [canDownloadCurrentContest, setCanDownloadCurrentContest] = useState(false);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load club data and contest data (if editing)
  useEffect(() => {
    const fetchData = async () => {
      try {
        // First, load club data
        const clubResponse = await api.get(`/club/${clubShortName}`, {
          headers: {
            'Authentication': user.authentication
          }
        });
        const clubData = clubResponse.data.result;
        setClub(clubData);

        // Set max uploads default based on club type (for create mode; overwritten below in edit mode)
        const defaultUploads = { free: 5, starter: 5, small: 10, medium: 15 };
        if (defaultUploads[clubData.clubType] !== undefined) {
          setContestMaxUploads(defaultUploads[clubData.clubType]);
        }

        // Check contest/gallery limit
        if (!isEditMode && clubData.contests && clubData.maxContests) {
          if (!isGalleryModeProp) {
            // Contest limit check — only count non-gallery contests
            const contestOnly = (clubData.contests || []).filter(ct => !ct.contestIsGallery);
            if (contestOnly.length >= clubData.maxContests) {
              setIsAtContestLimit(true);

              const sortedContests = [...contestOnly].sort((a, b) => a.contestVotesEnd - b.contestVotesEnd);
              const oldest = sortedContests[0];
              setOldestContest(oldest);

              const canDownload = clubData.userRole === 'owner' ||
                                 (oldest.contestCommittee && oldest.contestCommittee.includes(user.userEmail));
              setCanDownloadOldest(canDownload);
            }
          } else {
            // Gallery limit check — only count galleries; no auto-delete
            const galleryOnly = (clubData.contests || []).filter(ct => ct.contestIsGallery);
            const maxGalleries = clubData.maxGalleries ?? clubData.maxContests;
            if (galleryOnly.length >= maxGalleries) {
              setIsAtContestLimit(true);
            }
          }
        }

        if (isEditMode) {
          // Load contest data for editing
          const contestResponse = await api.get(`/club/${clubShortName}/contest/${contestShortName}`, {
            headers: {
              'Authentication': user.authentication
            }
          });
          const contestData = contestResponse.data.result;
          setContest(contestData);

          // Check if user has permission to edit this contest
          const canEdit = clubData.userRole === 'owner' ||
                         (contestData.contestCommittee && contestData.contestCommittee.includes(user.userEmail));

          if (!canEdit) {
            navigate('/dashboard');
            return;
          }

          // Check if user can download this contest
          const canDownloadContest = clubData.userRole === 'owner' ||
                                   (contestData.contestCommittee && contestData.contestCommittee.includes(user.userEmail));
          setCanDownloadCurrentContest(canDownloadContest);

          // Populate form with existing contest data
          setIsGallery(!!contestData.contestIsGallery);
          setContestName(contestData.contestName);
          setContestShortNameValue(contestData.contestShortName);
          setContestDescription(contestData.contestDescription || '');
          setContestGalleryEndDate(contestData.contestGalleryEndDate ? dayjs.unix(contestData.contestGalleryEndDate) : null);
          setContestUploadsStart(contestData.contestUploadsStart ? dayjs.unix(contestData.contestUploadsStart) : null);
          setContestUploadsEnd(contestData.contestUploadsEnd ? dayjs.unix(contestData.contestUploadsEnd) : null);
          setContestVotesStart(contestData.contestVotesStart ? dayjs.unix(contestData.contestVotesStart) : null);
          setContestVotesEnd(contestData.contestVotesEnd ? dayjs.unix(contestData.contestVotesEnd) : null);
          const maxForType = { starter: 5, small: 10, medium: 15 }[clubData.clubType] ?? 999;
          setContestMaxUploads(Math.min(contestData.contestMaxUploads || maxForType, maxForType));
          setContestMaxVotes(contestData.contestMaxVotes || 10);
          setContestCommittee(contestData.contestCommittee || []);
          setContestShowResults(contestData.contestShowResults || false);

          // For edit mode, short name is already taken (by this contest)
          setShortNameStatus('available');
        } else {
          // For create mode, check if user can create contests
          if (clubData.userRole !== 'owner') {
            navigate('/dashboard');
            return;
          }
        }
      } catch (err) {
        setSubmitError(`Failed to load ${isEditMode ? 'contest' : 'club'} data`);
        console.error(`Error fetching ${isEditMode ? 'contest' : 'club'}:`, err);
      } finally {
        setLoading(false);
      }
    };

    if (user?.authentication && clubShortName) {
      fetchData();
    }
  }, [user, clubShortName, contestShortName, isEditMode, navigate, isGalleryModeProp]);

  const validateShortName = (name) => {
    const validPattern = /^[a-z0-9-]+$/;
    return validPattern.test(name) && name.length > 0;
  };

  const checkShortNameAvailability = async (name) => {
    if (!name || !validateShortName(name)) {
      setShortNameStatus('invalid');
      setShortNameError('Short name must contain only lowercase letters, numbers, and dashes');
      return;
    }

    // In edit mode, if the name hasn't changed, it's still available
    if (isEditMode && name === contest?.contestShortName) {
      setShortNameStatus('available');
      setShortNameError('');
      return;
    }

    setShortNameStatus('checking');
    setShortNameError('');

    try {
      const response = await api.get(`/club/${clubShortName}/contestshortname?name=${encodeURIComponent(name)}`, {
        headers: {
          'Authentication': user.authentication
        }
      });

      if (response.status === 200) {
        setShortNameStatus('available');
        setShortNameError('');
      }
    } catch (error) {
      if (error.response?.status === 409) {
        setShortNameStatus('taken');
        setShortNameError('This short name is already taken');
      } else if (error.response?.status === 400) {
        setShortNameStatus('invalid');
        setShortNameError('Invalid short name');
      } else {
        setShortNameStatus('');
        setShortNameError('Error checking availability');
      }
    }
  };

  const handleShortNameChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setContestShortNameValue(value);

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    if (value) {
      const timer = setTimeout(() => {
        checkShortNameAvailability(value);
      }, 500);
      setDebounceTimer(timer);
    } else {
      setShortNameStatus('');
      setShortNameError('');
    }
  };

  // Convert date to epoch timestamp
  const dateToEpochStart = (date) => {
    if (!date) return null;
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return Math.floor(d.getTime() / 1000);
  };

  const dateToEpochEnd = (date) => {
    if (!date) return null;
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return Math.floor(d.getTime() / 1000);
  };

  const validateDates = (uploadsStart, uploadsEnd, votesStart, votesEnd) => {
    if (!uploadsStart && !uploadsEnd && !votesStart && !votesEnd) {
      setDateError('');
      return;
    }

    if (uploadsStart && uploadsEnd && uploadsStart > uploadsEnd) {
      setDateError('Upload end date must be equal to or after upload start date');
      return;
    }

    if (uploadsEnd && votesStart && votesStart <= uploadsEnd) {
      setDateError('Voting start date must be after upload end date');
      return;
    }

    if (votesStart && votesEnd && votesStart > votesEnd) {
      setDateError('Voting end date must be equal to or after voting start date');
      return;
    }

    setDateError('');
  };

  useEffect(() => {
    validateDates(contestUploadsStart, contestUploadsEnd, contestVotesStart, contestVotesEnd);
  }, [contestUploadsStart, contestUploadsEnd, contestVotesStart, contestVotesEnd]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const thing = isGallery ? 'gallery' : 'contest';

    if (!contestName.trim()) {
      setSubmitError(`${isGallery ? 'Gallery' : 'Contest'} name is required`);
      return;
    }

    if (!contestShortNameValue.trim()) {
      setSubmitError(`${isGallery ? 'Gallery' : 'Contest'} short name is required`);
      return;
    }

    if (!contestDescription.trim()) {
      setSubmitError(`${isGallery ? 'Gallery' : 'Contest'} description is required`);
      return;
    }

    if (shortNameStatus !== 'available') {
      setSubmitError('Please choose a valid and available short name');
      return;
    }

    if (!isGallery) {
      if (!contestUploadsStart || !contestUploadsEnd || !contestVotesStart || !contestVotesEnd) {
        setSubmitError('All dates are required');
        return;
      }
      if (!!dateError) {
        setSubmitError('Please fix date validation errors before submitting');
        return;
      }
    }

    setIsSubmitting(true);
    setSubmitError('');

    let data;
    if (isGallery) {
      data = {
        contestName: contestName.trim(),
        contestShortName: contestShortNameValue.trim(),
        contestDescription: contestDescription.trim(),
        contestCommittee: JSON.stringify(contestCommittee),
        ...(contestGalleryEndDate && { contestGalleryEndDate: dateToEpochEnd(contestGalleryEndDate) }),
        ...(!isEditMode && { isGallery: true }),
      };
    } else {
      const parsedMaxUploads = parseInt(contestMaxUploads, 10);
      data = {
        contestName: contestName.trim(),
        contestShortName: contestShortNameValue.trim(),
        contestDescription: contestDescription.trim(),
        contestUploadsStart: dateToEpochStart(contestUploadsStart),
        contestUploadsEnd: dateToEpochEnd(contestUploadsEnd),
        contestVotesStart: dateToEpochStart(contestVotesStart),
        contestVotesEnd: dateToEpochEnd(contestVotesEnd),
        contestMaxUploads: parsedMaxUploads,
        contestMaxVotes: contestMaxVotes,
        contestCommittee: JSON.stringify(contestCommittee),
        ...(isEditMode && { contestShowResults: contestShowResults }),
      };
    }

    if (isEditMode) {
      data.contestGuid = contest.contestGuid;
    }

    try {
      let response;
      if (isEditMode) {
        response = await api.post(`/club/${clubShortName}/contest/${contestShortName}`, qs.stringify(data), {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authentication': user.authentication
          },
        });
      } else {
        response = await api.post(`/club/${clubShortName}/contest`, qs.stringify(data), {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authentication': user.authentication
          },
        });
      }

      if (response.status === 200) {
        navigate(`/club/${clubShortName}/contest/${contestShortNameValue}`);
      } else {
        setSubmitError(response.statusText || `Failed to ${isEditMode ? 'update' : 'create'} ${thing}`);
      }
    } catch (error) {
      if (error.response?.status === 409) {
        setSubmitError(`You have reached the gallery limit for this club.`);
      } else {
        setSubmitError(error.response?.data || 'Network error. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getShortNameColor = () => {
    switch (shortNameStatus) {
      case 'available':
        return 'success';
      case 'taken':
      case 'invalid':
        return 'error';
      default:
        return 'primary';
    }
  };

  const getShortNameIcon = () => {
    switch (shortNameStatus) {
      case 'checking':
        return <CircularProgress size={20} />;
      case 'available':
        return <CheckCircleIcon color="success" />;
      case 'taken':
      case 'invalid':
        return <ErrorIcon color="error" />;
      default:
        return null;
    }
  };

  const handleDownloadOldest = () => {
    if (oldestContest) {
      window.open(`${process.env.REACT_APP_API_URL}/club/${clubShortName}/contest/${oldestContest.contestShortName}/download?token=${user.authentication}`, '_blank');
    }
  };

  const handleDownloadCurrentContest = () => {
    if (contest) {
      window.location.href = `${process.env.REACT_APP_API_URL}/club/${clubShortName}/contest/${contest.contestShortName}/download?token=${user.authentication}`;
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (contest && isEditMode && club.userRole === 'owner') {
      setIsDeleting(true);
      try {
        const response = await api.delete(`/club/${clubShortName}/contest/${contestShortName}`, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authentication': user.authentication
          },
        });

        if (response.status === 200) {
          navigate(`/dashboard`);
        } else {
          setSubmitError(response.statusText || `Failed to delete contest`);
        }
      } catch (error) {
        setSubmitError(error.response?.data || 'Network error. Please try again.');
      } finally {
        setIsDeleting(false);
        setDeleteDialogOpen(false);
      }
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading {isEditMode ? 'contest' : 'club'} data...
        </Typography>
      </Container>
    );
  }

  if (!club) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Club not found</Alert>
      </Container>
    );
  }

  if (isEditMode && !contest) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Contest not found</Alert>
      </Container>
    );
  }

  const maxUploadsLimit = tiers?.[club.clubType]?.contestPhotosPerMember ?? 999;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              {isEditMode
                ? `Edit ${isGallery ? 'Gallery' : 'Contest'}: ${contest?.contestName}`
                : `Create ${isGallery ? 'Gallery' : 'Contest'} for ${club.clubName}`}
            </Typography>

            {/* Action buttons for edit mode */}
            {isEditMode && (canDownloadCurrentContest || club.userRole === 'owner') && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {canDownloadCurrentContest && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadCurrentContest}
                      sx={{ ml: 2, flexShrink: 0 }}
                    >
                      Download {isGallery ? 'Gallery' : 'Contest'}
                    </Button>
                    {!isGallery && (
                      <Button
                        variant="outlined"
                        startIcon={<EmailIcon />}
                        onClick={() => navigate(`/club/${clubShortName}/contest/${contestShortName}/emailcontestants`)}
                        sx={{ ml: 2, flexShrink: 0 }}
                      >
                        EMAIL CONTESTANTS
                      </Button>
                    )}
                  </>
                )}
                {club.userRole === 'owner' && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteClick}
                    sx={{ ml: 2, flexShrink: 0 }}
                  >
                    Delete {isGallery ? 'Gallery' : 'Contest'}
                  </Button>
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" color="text.secondary">
              {isEditMode
                ? `Update your ${isGallery ? 'gallery' : 'contest'} information below.`
                : isGallery
                  ? 'Set up a gallery for your club members to share photos!'
                  : 'Set up a new photography contest for your club members!'
              }
            </Typography>
            {!isEditMode && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {`(You can edit ${isGallery ? 'gallery' : 'contest'} information after it is created.)`}
              </Typography>
            )}
          </Box>

          {/* Contest/Gallery Limit Warning */}
          {!isEditMode && isAtContestLimit && !isGallery && oldestContest && (
            <Alert
              severity="warning"
              icon={<WarningIcon />}
              sx={{ mb: 3 }}
            >
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Contest Limit Reached
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Creating this contest will automatically delete your oldest contest: "{oldestContest.contestName}".
              </Typography>
              {canDownloadOldest && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    Download photos and voting records before proceeding:
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadOldest}
                    sx={{ ml: 1 }}
                  >
                    Download "{oldestContest.contestName}"
                  </Button>
                </Box>
              )}
            </Alert>
          )}
          {!isEditMode && isAtContestLimit && isGallery && (
            <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Gallery Limit Reached
              </Typography>
              <Typography variant="body2">
                Your club has reached its gallery limit. Delete an existing gallery before creating a new one.
              </Typography>
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label={isGallery ? 'Gallery Name' : 'Contest Name'}
              value={contestName}
              onChange={(e) => setContestName(e.target.value)}
              margin="normal"
              required
              placeholder={isGallery ? 'e.g., Member Showcase' : 'e.g., Spring Nature Contest'}
              inputProps={{ maxLength: 100 }}
            />

            <TextField
              fullWidth
              label={isGallery ? 'Gallery Short Name' : 'Contest Short Name'}
              value={contestShortNameValue}
              onChange={handleShortNameChange}
              margin="normal"
              required
              placeholder={isGallery ? 'e.g., member-showcase' : 'e.g., spring-nature'}
              helperText={`This will be part of your ${isGallery ? 'gallery' : 'contest'}'s URL`}
              color={getShortNameColor()}
              disabled={isEditMode}
              InputProps={{
                endAdornment: !isEditMode ? getShortNameIcon() : null
              }}
            />

            {!isEditMode && shortNameError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {shortNameError}
              </Typography>
            )}

            {!isEditMode && shortNameStatus === 'available' && (
              <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                Great! This short name is available.
              </Typography>
            )}

            <TextField
              fullWidth
              label={isGallery ? 'Gallery Description' : 'Contest Description'}
              value={contestDescription}
              onChange={(e) => setContestDescription(e.target.value)}
              margin="normal"
              required
              multiline
              rows={4}
              placeholder={isGallery
                ? 'Describe what members can share in this gallery...'
                : 'Describe your contest theme, rules, and guidelines...'}
              inputProps={{ maxLength: 2000 }}
            />

            {isGallery ? (
              <Box sx={{ mt: 2 }}>
                <DatePicker
                  label="Gallery End Date (Optional)"
                  value={contestGalleryEndDate}
                  onChange={setContestGalleryEndDate}
                  slotProps={{ textField: { InputLabelProps: { shrink: true }, helperText: 'Leave blank for no end date' } }}
                />
              </Box>
            ) : (
              <>
                {dateError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {dateError}
                  </Alert>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
                  <DatePicker
                    label="Upload Start Date"
                    value={contestUploadsStart}
                    onChange={setContestUploadsStart}
                    slotProps={{ textField: { required: true, InputLabelProps: { shrink: true } } }}
                  />

                  <DatePicker
                    label="Upload End Date"
                    value={contestUploadsEnd}
                    onChange={setContestUploadsEnd}
                    slotProps={{ textField: { required: true, InputLabelProps: { shrink: true } } }}
                  />

                  <DatePicker
                    label="Voting Start Date"
                    value={contestVotesStart}
                    onChange={setContestVotesStart}
                    slotProps={{ textField: { required: true, InputLabelProps: { shrink: true } } }}
                  />

                  <DatePicker
                    label="Voting End Date"
                    value={contestVotesEnd}
                    onChange={setContestVotesEnd}
                    slotProps={{ textField: { required: true, InputLabelProps: { shrink: true } } }}
                  />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
                  <TextField
                    label="Max Uploads per Member"
                    type="text"
                    inputMode="numeric"
                    value={contestMaxUploads}
                    onChange={(e) => setContestMaxUploads(e.target.value)}
                    helperText={club.clubType === 'large' ? 'No maximum for large club' : `Maximum: ${maxUploadsLimit} for ${club.clubType} club`}
                    required
                  />

                  <TextField
                    label="Max Votes per Member"
                    type="text"
                    inputMode="numeric"
                    value={contestMaxVotes}
                    onChange={(e) => setContestMaxVotes(e.target.value)}
                    required
                  />
                </Box>

                {isEditMode && (
                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={contestShowResults}
                          onChange={(e) => setContestShowResults(e.target.checked)}
                        />
                      }
                      label="Show contest results to members"
                    />
                  </Box>
                )}
              </>
            )}

            <Box sx={{ mt: 2 }}>
              <Autocomplete
                multiple
                freeSolo
                options={club.clubMembers || []}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return `${option.userFullName} <${option.userEmail}>`;
                }}
                value={contestCommittee}
                onChange={(event, newValue) => {
                  setContestCommittee(newValue.map(item => {
                    if (typeof item === 'string') return item;
                    return item.userEmail;
                  }));
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={isGallery ? 'Gallery Moderators (Optional)' : 'Contest Committee (Optional)'}
                    placeholder="Type email addresses or select members"
                    helperText={isGallery
                      ? 'Moderators can delete any photo from the gallery'
                      : 'Committee members can see all photos before voting starts'}
                    InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                  />
                )}
              />
            </Box>

            {submitError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {submitError}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={
                isSubmitting ||
                (!isEditMode && shortNameStatus !== 'available') ||
                !contestName.trim() ||
                !contestDescription.trim() ||
                (!isGallery && (!contestUploadsStart || !contestUploadsEnd || !contestVotesStart || !contestVotesEnd)) ||
                (!isGallery && !!dateError) ||
                (!isGallery && (() => { const n = parseInt(contestMaxUploads, 10); return !Number.isInteger(n) || n < 1 || n > maxUploadsLimit; })()) ||
                (!isGallery && (() => { const n = parseInt(contestMaxVotes, 10); return !Number.isInteger(n) || n < 1; })()) ||
                (!isEditMode && isAtContestLimit && isGallery)
              }
              sx={{ mt: 3 }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {isEditMode
                    ? `Updating ${isGallery ? 'Gallery' : 'Contest'}...`
                    : `Creating ${isGallery ? 'Gallery' : 'Contest'}...`}
                </>
              ) : isEditMode ? (
                `Update ${isGallery ? 'Gallery' : 'Contest'}`
              ) : !isGallery && isAtContestLimit && oldestContest ? (
                'DELETE OLDEST CONTEST AND CREATE'
              ) : (
                `Create ${isGallery ? 'Gallery' : 'Contest'}`
              )}
            </Button>
          </Box>
        </Paper>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Delete {isGallery ? 'Gallery' : 'Contest'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the {isGallery ? 'gallery' : 'contest'} "{contest?.contestName}"?
            </DialogContentText>
            <DialogContentText sx={{ mt: 2, fontWeight: 'bold', color: 'error.main' }}>
              This action cannot be undone. All photos{isGallery ? '' : ', votes,'} and {isGallery ? 'gallery' : 'contest'} data will be permanently deleted.
            </DialogContentText>
            {!isGallery && (
              <DialogContentText sx={{ mt: 2 }}>
                If you need to preserve the contest data, please download it before proceeding.
              </DialogContentText>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleDeleteCancel}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={isDeleting}
              startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              {isDeleting ? 'Deleting...' : `Delete ${isGallery ? 'Gallery' : 'Contest'}`}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default ContestForm;